import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FullPageLoader } from '@/components/states';
import { useSession } from '@/lib/auth';

/**
 * Cognito redirects here with `?code=…&state=…`. react-oidc-context detects those params on
 * mount and performs the PKCE code exchange itself, so all this route has to do is wait and
 * then hand control back to the catalogue.
 */
export function AuthCallbackPage() {
  const session = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (session.status === 'authenticated') {
      void navigate('/', { replace: true });
    }
  }, [session.status, navigate]);

  if (session.status === 'error' && session.error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div
          role="alert"
          className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-center"
        >
          <AlertCircle aria-hidden="true" className="h-8 w-8 text-destructive" />
          <h1 className="text-lg font-semibold">Sign-in did not complete</h1>
          <p className="text-sm text-muted-foreground">{session.error.message}</p>
          <Button
            type="button"
            onClick={() => {
              session.clearError();
              void navigate('/', { replace: true });
            }}
          >
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  if (session.status === 'anonymous') {
    // The exchange finished without producing a session (for example the user cancelled).
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <h1 className="text-lg font-semibold">Sign-in was not completed</h1>
          <Button type="button" onClick={() => void navigate('/', { replace: true })}>
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  return <FullPageLoader label="Completing sign-in…" />;
}
